"use client"

import { useState, useEffect } from "react"
import { Heart, Brain, Scan, ChevronRight, Menu, X, Sun, Moon } from "lucide-react"
import ImageCompare from './components/ImageCompare';
import ImageStack from './components/ImageStack';
import lowResImage from './assets/images/low.png';
import highResImage from './assets/images/high.png';
import partialImage from './assets/images/partial.jpg';
import abdomenImage from './assets/images/abdomen.jpg';
import headImage from './assets/images/head.jpg';
import EmailModal from './components/EmailModal';
import { track } from '@vercel/analytics';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark")
    } else {
      document.documentElement.classList.remove("dark")
    }
  }, [isDarkMode])

  const handleButtonClick = () => {
    track('beta-signup-clicked');
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black transition-colors duration-300">
      {/* Enhanced Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? "glass-effect" : "bg-transparent"}`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-20">
            <div className="flex-shrink-0">
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="text-2xl font-semibold gradient-text cursor-pointer"
              >
                Birthsense AI
              </a>
            </div>
            <div className="hidden md:flex items-center space-x-10">
              <a
                href="#features"
                className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:gradient-text transition-all"
              >
                Features
              </a>
              <a
                href="#technology"
                className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:gradient-text transition-all"
              >
                Technology
              </a>
              <a
                href="#impact"
                className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:gradient-text transition-all"
              >
                Impact
              </a>
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                {isDarkMode ? <Sun className="w-5 h-5 text-gray-300" /> : <Moon className="w-5 h-5 text-gray-700" />}
              </button>
              <button
                onClick={handleButtonClick}
                className="apple-button bg-[#0A84FF] text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-[#0A84FF]/90 transition-all"
              >
                Get Early Access
              </button>
            </div>
            <div className="md:hidden flex items-center space-x-4">
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                {isDarkMode ? <Sun className="w-5 h-5 text-gray-300" /> : <Moon className="w-5 h-5 text-gray-700" />}
              </button>
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-700 dark:text-gray-300">
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-t dark:border-gray-800">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <a
                href="#features"
                className="block px-3 py-2 text-gray-700 dark:text-gray-300 hover:text-[#0A84FF] dark:hover:text-[#0A84FF] transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Features
              </a>
              <a
                href="#technology"
                className="block px-3 py-2 text-gray-700 dark:text-gray-300 hover:text-[#0A84FF] dark:hover:text-[#0A84FF] transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Technology
              </a>
              <a
                href="#impact"
                className="block px-3 py-2 text-gray-700 dark:text-gray-300 hover:text-[#0A84FF] dark:hover:text-[#0A84FF] transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Impact
              </a>
              <button
                onClick={handleButtonClick}
                className="w-full mt-2 bg-gradient-to-r from-[#0A84FF] to-[#6366f1] text-white px-6 py-2 rounded-full hover:opacity-90 transition-opacity"
              >
                Get Early Access
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Enhanced Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Add decorative gradients */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          {/* Colorful gradient circles with reduced opacity */}
          <div style={{ opacity: 0.6 }}  className="absolute top-20 left-1/4 w-[35rem] h-[35rem] bg-[#FF3366]/10 rounded-full mix-blend-normal dark:mix-blend-plus-lighter filter blur-[60px] animate-blob" />
          <div style={{ opacity: 0.6 }}  className="absolute -top-20 right-1/4 w-[25rem] h-[25rem] bg-[#7C3AED]/15 rounded-full mix-blend-normal dark:mix-blend-plus-lighter filter blur-[60px] animate-blob animation-delay-2000" />
          <div style={{ opacity: 0.6 }}  className="absolute bottom-0 left-1/3 w-[30rem] h-[30rem] bg-[#0EA5E9]/15 rounded-full mix-blend-normal dark:mix-blend-plus-lighter filter blur-[60px] animate-blob animation-delay-4000" />
          <div style={{ opacity: 0.6 }}  className="absolute top-1/3 right-1/4 w-[28rem] h-[28rem] bg-[#F59E0B]/10 rounded-full mix-blend-normal dark:mix-blend-plus-lighter filter blur-[60px] animate-blob animation-delay-3000" />
          <div style={{ opacity: 0.6 }}  className="absolute bottom-1/4 left-1/2 w-[32rem] h-[32rem] bg-[#10B981]/10 rounded-full mix-blend-normal dark:mix-blend-plus-lighter filter blur-[60px] animate-blob animation-delay-5000" />

          {/* Additional color layers with reduced opacity */}
         
          {/* Colorful gradient circles with reduced opacity */}
          {/* <div className="absolute top-20 left-1/4 w-[35rem] h-[35rem] bg-[#FF3366]/10 rounded-full mix-blend-normal dark:mix-blend-plus-lighter filter blur-[60px] animate-blob"
            style={{ opacity: 0.001 }} />
          <div className="absolute -top-20 right-1/4 w-[25rem] h-[25rem] bg-[#7C3AED]/15 rounded-full mix-blend-normal dark:mix-blend-plus-lighter filter blur-[60px] animate-floating animation-delay-2000"
            style={{ opacity: 0.001 }} />
          <div className="absolute bottom-0 left-1/3 w-[30rem] h-[30rem] bg-[#0EA5E9]/15 rounded-full mix-blend-normal dark:mix-blend-plus-lighter filter blur-[60px] animate-blob animation-delay-4000"
            style={{ opacity: 0.001 }} />
          <div className="absolute top-1/3 right-1/4 w-[28rem] h-[28rem] bg-[#F59E0B]/10 rounded-full mix-blend-normal dark:mix-blend-plus-lighter filter blur-[60px] animate-floating animation-delay-3000"
            style={{ opacity: 0.001 }} />
          <div className="absolute bottom-1/4 left-1/2 w-[32rem] h-[32rem] bg-[#10B981]/10 rounded-full mix-blend-normal dark:mix-blend-plus-lighter filter blur-[60px] animate-blob animation-delay-5000"
            style={{ opacity: 0.001 }} /> */}
        </div>

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center">
            <h1 className="text-balance text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight mb-8">
              <span className="block text-gray-900 dark:text-white mb-4">Democratizing</span>
              <span className="gradient-text">Maternal Healthcare with AI</span>
            </h1>
            <p className="mt-8 text-xl md:text-2xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto text-balance leading-relaxed">
              Building a foundation model for ultrasound and MRI imaging to bring quality maternal care to everyone, everywhere.
            </p>
            <div className="mt-12">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('image-comparison').scrollIntoView({ 
                    behavior: 'smooth' 
                  });
                  track('see-in-action-clicked');
                }}
                className="apple-button bg-[#0A84FF] text-white px-10 py-5 rounded-full text-lg font-medium hover:bg-[#0A84FF]/90 transition-all inline-flex items-center"
              >
                See It in Action <ChevronRight className="ml-2" size={20} />
              </button>
            </div>
          </div>

          {/* Enhanced Product Showcase */}
          <div id="image-comparison" className="mt-24 relative parallax-container">
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A84FF]/10 to-[#6366f1]/10 rounded-[2.5rem] blur-3xl transform rotate-3" />
            <div className="absolute inset-0 bg-gradient-to-l from-[#0A84FF]/10 to-[#6366f1]/10 rounded-[2.5rem] blur-3xl transform -rotate-3" />
            <div className="relative w-full h-[600px] rounded-[2.5rem] shadow-2xl transform -rotate-1 parallax-element">
              <p className="text-center text-gray-600 dark:text-gray-400 mb-8">
                Slide to compare standard and AI-enhanced ultrasound imaging
              </p>
              <ImageCompare
                beforeImage={lowResImage}
                afterImage={highResImage}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Features Section */}
      <section id="features" className="py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-24">
            <h2 className="text-balance text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-8">
              We're Tackling a <br />Global Problem
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-balance">
              Low-resource settings lack access to quality imaging equipment. Our AI makes existing equipment more powerful.
            </p>
          </div>

          <div className="grid gap-12 md:grid-cols-3">
            {[
              {
                icon: <Scan className="w-8 h-8 text-[#0A84FF]" />,
                title: "Guidance + Enhancement",
                description:
                  "Our AI guides providers to capture better images, then applies super-resolution to transform low-quality scans into diagnostic-quality visualizations.",
              },
              {
                icon: <Brain className="w-8 h-8 text-[#0A84FF]" />,
                title: "Segmentation",
                description:
                  "Our foundation model automatically identifies and segments critical anatomical structures from enhanced images for precise measurements.",
              },
              {
                icon: <Heart className="w-8 h-8 text-[#0A84FF]" />,
                title: "Anomaly Detection",
                description:
                  "We identify potential complications and abnormalities that might be missed in low-quality imaging, enabling earlier interventions.",
              },
            ].map((feature, index) => (
              <div key={index} className="relative group h-[400px]">
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A84FF]/5 to-[#6366f1]/5 rounded-3xl blur-2xl transition-all duration-300 group-hover:blur-3xl" />
                <div className="glass-effect relative p-10 rounded-3xl h-full flex flex-col transition-all duration-300 group-hover:translate-y-[-5px]">
                  <div className="w-16 h-16 bg-gradient-to-r from-[#0A84FF]/10 to-[#6366f1]/10 rounded-2xl flex items-center justify-center mb-8">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">{feature.title}</h3>
                  <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Technology Section */}
      <section id="technology" className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A84FF]/5 to-transparent dark:from-[#0A84FF]/10 dark:to-transparent" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid md:grid-cols-2 gap-32 items-center">
            <div>
              <h2 className="text-balance text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white leading-tight mb-8">
                One Foundation Model, <br />
                <span className="gradient-text">Multiple Solutions</span>
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed mb-12">
                We're building a unified AI model that enhances imaging quality, guides capture, and detects anomalies - all from a single foundation.
              </p>
              <div className="space-y-4">
                {[
                  {
                    icon: <div className="w-10 h-10 bg-[#0A84FF]/10 rounded-xl flex items-center justify-center">
                      <Scan className="w-5 h-5 text-[#0A84FF]" />
                    </div>,
                    title: "Works with Existing Hardware",
                    description: "Our software solution enhances images from any ultrasound or MRI device, no equipment upgrades needed.",
                    badge: "Hardware"
                  },
                  {
                    icon: <div className="w-10 h-10 bg-[#0A84FF]/10 rounded-xl flex items-center justify-center">
                      <Brain className="w-5 h-5 text-[#0A84FF]" />
                    </div>,
                    title: "Continuous Learning",
                    description: "Our model improves with each image processed, getting better at identifying structures and anomalies over time.",
                    badge: "AI"
                  }
                ].map((item, index) => (
                  <div
                    key={index}
                    className="group relative glass-effect rounded-2xl p-4 transition-all duration-300 hover:translate-y-[-2px]"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0A84FF]/5 to-[#6366f1]/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-start space-x-4 relative">
                      {item.icon}
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-base font-semibold text-gray-900 dark:text-white">{item.title}</h3>
                          <span className="px-2 py-0.5 text-xs font-medium text-[#0A84FF] bg-[#0A84FF]/10 rounded-full">
                            {item.badge}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative parallax-container">
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A84FF]/20 to-[#6366f1]/20 rounded-[2.5rem] blur-3xl" />
              <div className="relative h-[400px] overflow-hidden">
                <div className="absolute inset-0 scale-[0.85]">
                  <ImageStack
                    images={[partialImage, abdomenImage, headImage]}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Impact Section */}
      <section id="impact" className="py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center max-w-3xl mx-auto mb-24">
            <h2 className="text-balance text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-8">
              The Problem We're Solving
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Maternal mortality is a global crisis that technology can help address
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "300K+", label: "Maternal deaths annually" },
              { value: "94%", label: "Deaths in low-resource areas" },
              { value: "2/3", label: "Deaths preventable with care" },
              { value: "$200B+", label: "Global market opportunity" },
            ].map((stat, index) => (
              <div key={index} className="relative group h-[200px]">
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A84FF]/10 to-[#6366f1]/10 rounded-2xl blur-xl transition-all duration-300 group-hover:blur-2xl" />
                <div className="glass-effect relative p-10 rounded-2xl h-full flex flex-col items-center justify-center transition-all duration-300 group-hover:translate-y-[-5px]">
                  <div className="text-5xl font-bold gradient-text mb-4">{stat.value}</div>
                  <p className="text-lg text-gray-600 dark:text-gray-400">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A84FF] to-[#6366f1] rounded-[2.5rem] blur-3xl opacity-30" />
            <div className="relative glass-effect rounded-[2.5rem] p-20 overflow-hidden">
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-balance text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-8">
                  Help Us Revolutionize Maternal Healthcare
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-400 mb-12">
                  We're seeking beta partners, research collaborators, and investors who share our vision.
                </p>
                <button
                  onClick={handleButtonClick}
                  className="apple-button bg-[#0A84FF] text-white px-12 py-6 rounded-full text-xl font-medium hover:bg-[#0A84FF]/90 transition-all"
                >
                  Get Early Access
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Get in Touch Footer */}
      <footer className="py-10 text-center">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Want to learn more about Birthsense AI?
          </p>
          <a 
            href="https://www.linkedin.com/in/samdon/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center text-[#0A84FF] hover:underline font-medium"
          >
            Get in Touch
            <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="mt-6 text-sm text-gray-500 dark:text-gray-500">
            © {new Date().getFullYear()} Birthsense AI. All rights reserved.
          </p>
        </div>
      </footer>

      <EmailModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  )
}

export default App

